// Lightweight, server-sorted table for the v2 dashboard top-partner / locations tables.
// Sorting is delegated to the caller (onSort) since the backend returns pre-sorted rows.
// By default only the first `initialVisibleRows` (5) rows are shown, with a "show all" toggle.
import React, { ReactNode, useState } from 'react';
import SvgCaretUp from '@icons/caret-up.svg?react';
import SvgCaretDown from '@icons/caret-down.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import DashboardEmptyState from './DashboardEmptyState.tsx';
import { SortDir } from '../dashboardTypes';

export interface DashboardColumn<Row, SortKey extends string> {
  key: string;
  label: string;
  // When set, the header is clickable and triggers onSort(sortKey).
  sortKey?: SortKey;
  align?: 'left' | 'right';
  render: (row: Row) => ReactNode;
}

interface DashboardTableProps<Row, SortKey extends string> {
  columns: DashboardColumn<Row, SortKey>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  sortBy?: SortKey;
  sortDir?: SortDir;
  onSort?: (sortKey: SortKey) => void;
  emptyLabel?: string;
  initialVisibleRows?: number;
}

function DashboardTable<Row, SortKey extends string>({ columns, rows, rowKey, sortBy, sortDir, onSort, emptyLabel, initialVisibleRows = 5 }: DashboardTableProps<Row, SortKey>) {
  const { tr } = useTranslationLgs();
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? rows : rows.slice(0, initialVisibleRows);

  return (
    <div className="flex flex-col gap-2">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {columns.map(col => {
              const sortable = !!col.sortKey && !!onSort;
              const active = col.sortKey && sortBy === col.sortKey;
              return (
                <th
                  key={col.key}
                  className={`bg-content-quaternary py-2 px-4 font-normal text-content-secondary text-sm first:rounded-l-md last:rounded-r-md ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                    sortable ? 'cursor-pointer select-none' : ''
                  }`}
                  onClick={sortable ? () => onSort!(col.sortKey!) : undefined}>
                  <span className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                    {col.label}
                    {active && (sortDir === SortDir.ASC ? <SvgCaretUp className="w-2.5 h-2.5" /> : <SvgCaretDown className="w-2.5 h-2.5" />)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length}>
                <DashboardEmptyState height={200} label={emptyLabel} />
              </td>
            </tr>
          )}
          {visibleRows.map((row, idx) => (
            <tr key={rowKey(row, idx)}>
              {columns.map(col => (
                <td key={col.key} className={`py-3 px-4 text-content-primary border-b [border-bottom-style:solid] border-content-quaternary ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > initialVisibleRows && (
        <div className="flex justify-center">
          <RingilButton
            mode="text-only"
            size="small"
            label={showAll ? tr('dashboard.showLess', 'Show less') : tr('dashboard.showAll', 'Show all ({{count}})', { count: rows.length })}
            onClick={() => setShowAll(prev => !prev)}
          />
        </div>
      )}
    </div>
  );
}

export default DashboardTable;